import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import * as Menu from "@/components/ui/menu";
import { Text } from "@/components/ui/text";
import { api } from "@api";
import { AppDrawer, appDialogConfirm } from "@components";
import { DataGuard } from "@components/DataGuard";
import LayoutHeader from "@components/LayoutHeader";
import Panel from "@components/Panel";
import { A, useNavigate, useParams } from "@solidjs/router";
import { useAuth, userId } from "@state/auth";
import { useGoRead } from "@state/goRead/state";
import { useQuery } from "@tanstack/solid-query";
import dayjs from "dayjs";
import { ArrowLeft, Edit, EllipsisVertical, Trash2 } from "lucide-solid";
import { Show } from "solid-js";
import { Portal } from "solid-js/web";
import { useService } from "solid-services";
import { Box, Flex, Grid } from "styled-system/jsx";

import BubbleComments from "@features/Comments/Bubble";
import { TOPICS } from "@features/communityContent/topics";
import Chip from "../../Setups/Chip";
import Create from "../List/Create";
import { useState } from "../List/state";
import { readMinutes } from "../utils";
import Body from "./Body";
import Poster from "./Poster";
import { goReadCommentsAdapter } from "./goReadCommentsAdapter";

export default function SingleGoReadPost() {
  const params = useParams();
  const navigate = useNavigate();
  const { deletePost } = useService(useGoRead)();
  const { editPost, setEditPost, isCreateFormOpen, setIsCreateFormOpen } = useService(useState)();
  const { isAdmin } = useService(useAuth)();

  const postQuery = useQuery(() => ({
    queryKey: ['go-read-post', params.id],
    queryFn: async () => {
      const { data, error } = await api()
        .from('go_read_posts')
        .select(`
          *,
          author:profile_id (
            id,
            username,
            profile_private (role)
          )
        `)
        .eq('id', params.id)
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    enabled: !!params.id,
  }));

  return (
    <DataGuard
      query={postQuery}
    >
      {(post) => (
        <>
          <LayoutHeader
            title={post?.title}
            subTitle={dayjs(post?.created_at).format('dddd, MMMM D, YYYY')}
            rightContent={
              <Flex gap='2' alignItems='center'>
                <A href={'/go-read'}>
                  <Button size='xs' variant='outline'>
                    <ArrowLeft />
                    Back to Go Read
                  </Button>
                </A>

                <Show when={isAdmin() || post?.profile_id === userId()}>
                  <Menu.Root
                    onSelect={(details) => {
                      if (details.value === 'edit') setEditPost(post);
                      if (details.value === 'delete') {
                        appDialogConfirm(async () => {
                          await deletePost(post.id);
                          navigate('/go-read');
                        }, 'Are you sure you want to delete this post?')();
                      }
                    }}
                  >
                    <Menu.Trigger
                      asChild={(triggerProps) => (
                        <IconButton variant='outline' size='xs' {...triggerProps()}>
                          <EllipsisVertical size={16} />
                        </IconButton>
                      )}
                    />
                    <Portal>
                      <Menu.Positioner>
                        <Menu.Content>
                          <Menu.Item minH={8} value="edit">
                            <Edit size={14} />
                            <Text textStyle="xs">Edit</Text>
                          </Menu.Item>
                          <Menu.Item minH={8} value="delete" color="error.10">
                            <Trash2 size={14} />
                            <Text textStyle="xs">Delete</Text>
                          </Menu.Item>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Portal>
                  </Menu.Root>
                </Show>
              </Flex>
            }
          />

          <Panel height="calc(var(--vh) * 100 - 102px)" overflowY="auto">
            <Grid gap='0' width="full" maxW="760px" py="30px" mx="auto" px={{ base: '24px', smDown: '16px' }}>
              <Flex alignItems='center' gap='9px' mb='14px'>
                <Chip color={TOPICS[post?.topic].color}>{TOPICS[post?.topic].label}</Chip>
                <Text fontSize='11.5px' color='editorialInk3'>{readMinutes(post?.content ?? '')} min read</Text>
              </Flex>

              <Text
                as='h1'
                fontSize='32px'
                fontWeight='800'
                letterSpacing='-.025em'
                lineHeight='1.18'
                color='editorialInk'
                mb='18px'
              >
                {post?.title}
              </Text>

              <Poster
                username={post?.author?.username}
                id={post?.author?.id}
                rank={(post?.author?.profile_private as any)?.role}
                date={post?.created_at}
              />

              <Body content={post?.content ?? ''} />

              <Box borderTopWidth='1px' borderTopStyle='solid' borderTopColor='var(--colors-editorial-hair)' pt='26px'>
                <BubbleComments
                  entityId={post?.id}
                  adapter={goReadCommentsAdapter}
                />
              </Box>
            </Grid>
          </Panel>

          <AppDrawer
            open={isCreateFormOpen()}
            onOpenChange={({ open }) => setIsCreateFormOpen(open)}
            title={editPost() ? 'Edit Post' : 'Write a post'}
            size='md'
          >
            <Create onSuccess={() => setIsCreateFormOpen(false)} />
          </AppDrawer>
        </>
      )}
    </DataGuard>
  );
}
